import React, { useState, useMemo } from 'react';
import { GraduationCap, Search, Filter, Mail, Phone, Calendar, Building2, BookOpen, ShieldCheck, Clock, CheckCircle2, AlertCircle, Info, X, Key, ChevronUp, ChevronDown, ShieldOff, ShieldAlert, Lock, Ban } from '../DeskeyIcons';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { TwoFAConfirmModal } from '../ui/TwoFAConfirmModal';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function api(path, opts = {}) {
  const token = localStorage.getItem('desky_token');
  const headers = { 'Authorization': `Bearer ${token}`, ...opts.headers };
  if (!(opts.body instanceof FormData)) headers['Content-Type'] = 'application/json';
  return fetch(`${API_URL}/api${path}`, { headers, ...opts }).then(r => r.json());
}

const StatusBadge = ({ status }) => {
  const styles = {
    active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    none: 'bg-gray-50 text-gray-400 border-gray-200',
  };
  return (
    <span className={`text-[10px] px-2.5 py-1 rounded-full font-semibold border ${styles[status] || styles.none}`}>
      {status === 'active' ? 'Active' : status === 'pending' ? 'Pending' : 'No Licence'}
    </span>
  );
};

const InfoRow = ({ icon: Icon, label, value, accent, mono }) => (
  <div className="flex items-center gap-3 py-3">
    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${accent}10` }}>
      <Icon className="w-4 h-4" style={{ color: accent }} />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">{label}</p>
      <p className={`text-sm text-gray-800 truncate ${mono ? 'font-mono text-xs' : ''}`}>{value}</p>
    </div>
  </div>
);

const ProfileModal = ({ person, accent, onClose, onBlock, onUnblock, readOnly }) => {
  const A = accent;
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [tfaCode, setTfaCode] = useState('');
  const [devCode, setDevCode] = useState('');
  const [tfaSent, setTfaSent] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [unblocking, setUnblocking] = useState(false);

  if (!person) return null;

  const initial = (person.full_name || '?')[0].toUpperCase();
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' }) : '—';
  const isBlocked = person.blocked === true;

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const send2FA = async () => {
    try {
      const token = localStorage.getItem('desky_token');
      const me = await fetch(`${API_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json());
      const res = await fetch(`${API_URL}/api/auth/2fa/send`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email: me.email })
      }).then(r => r.json());
      setDevCode(res.dev_code || '');
      setTfaSent(true);
      toast.success('2FA code sent');
    } catch { toast.error('Failed to send 2FA code'); }
  };

  const handleBlock = async () => {
    if (!blockReason.trim()) return toast.error('Please enter a reason');
    if (!tfaCode.trim()) return toast.error('Please enter the 2FA code');
    setBlocking(true);
    try {
      const token = localStorage.getItem('desky_token');
      const res = await fetch(`${API_URL}/api/institution/students/${person.id}/block`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reason: blockReason, twofa_code: tfaCode })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success('Student blocked');
      if (onBlock) onBlock(person.id);
      onClose();
    } catch (e) { toast.error(e.message); }
    setBlocking(false);
  };

  const handleUnblock = async () => {
    setUnblocking(true);
    try {
      const token = localStorage.getItem('desky_token');
      const res = await fetch(`${API_URL}/api/institution/students/${person.id}/unblock`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success('Student unblocked');
      if (onUnblock) onUnblock(person.id);
      onClose();
    } catch (e) { toast.error(e.message); }
    setUnblocking(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="profile-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto">
        {/* Header band */}
        <div className="h-28 relative" style={{ background: isBlocked ? 'linear-gradient(135deg, #ef4444, #dc2626)' : `linear-gradient(135deg, ${A}, ${A}cc)` }}>
          <button onClick={onClose} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors" data-testid="close-profile-modal">
            <X className="w-4 h-4 text-white" />
          </button>
          {isBlocked && (
            <div className="absolute bottom-3 left-4 flex items-center gap-1.5 bg-white/20 rounded-full px-3 py-1">
              <ShieldOff className="w-3.5 h-3.5 text-white" />
              <span className="text-[10px] text-white font-semibold uppercase tracking-wider">Blocked</span>
            </div>
          )}
        </div>

        {/* Avatar */}
        <div className="flex justify-center -mt-14">
          {person.profile_photo ? (
            <img src={person.profile_photo} alt="" className="w-28 h-28 rounded-full border-4 border-white shadow-lg object-cover" />
          ) : (
            <div className="w-28 h-28 rounded-full border-4 border-white shadow-lg flex items-center justify-center text-white text-3xl font-bold" style={{ background: isBlocked ? '#ef4444' : A }}>
              {initial}
            </div>
          )}
        </div>

        {/* Name + Role */}
        <div className="text-center mt-3 px-6">
          <h3 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{person.full_name}</h3>
          <p className="text-xs text-gray-400 mt-0.5 uppercase tracking-wider font-medium">Student</p>
          <div className="mt-1.5 flex items-center justify-center gap-2">
            <StatusBadge status={person.licence_status || 'none'} />
            {isBlocked && <span className="text-[10px] px-2.5 py-1 rounded-full font-semibold border bg-red-50 text-red-700 border-red-200">Blocked</span>}
          </div>
        </div>

        {/* Blocked reason banner */}
        {isBlocked && person.blocked_reason && (
          <div className="mx-6 mt-4 p-3 rounded-xl bg-red-50 border border-red-100">
            <p className="text-[10px] text-red-400 font-semibold uppercase tracking-wider mb-1">Block Reason</p>
            <p className="text-sm text-red-700">{person.blocked_reason}</p>
            {person.blocked_at && <p className="text-[10px] text-red-400 mt-1">Blocked on {formatDate(person.blocked_at)}</p>}
          </div>
        )}

        {/* Bio */}
        <div className="px-6 mt-4">
          <p className="text-sm text-gray-500 text-center italic leading-relaxed">
            {person.bio || 'No bio added yet.'}
          </p>
        </div>

        {/* Info grid */}
        <div className="px-6 mt-5 space-y-0 divide-y divide-gray-100">
          <InfoRow icon={Mail} label="Email" value={person.email} accent={A} />
          <InfoRow icon={Phone} label="Phone" value={person.phone || 'Not provided'} accent={A} />
          <InfoRow icon={Building2} label="Departments" value={person.department_names || 'None'} accent={A} />
          <InfoRow icon={BookOpen} label="Enrolled Classes" value={`${person.enrolled_classes || 0} class${(person.enrolled_classes || 0) !== 1 ? 'es' : ''}`} accent={A} />
          {(person.pending_classes || 0) > 0 && <InfoRow icon={Clock} label="Pending Enrollment" value={`${person.pending_classes} pending`} accent={A} />}
          <InfoRow icon={Calendar} label="Joined" value={formatDate(person.created_at)} accent={A} />
          {person.licence_key && <InfoRow icon={Key} label="Licence Key" value={person.licence_key} accent={A} mono />}
        </div>

        {/* Department + enrollment tags */}
        <div className="px-6 py-4 space-y-4">
          {(person.departments || []).length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Departments</p>
              <div className="flex flex-wrap gap-1.5">
                {person.departments.map(d => (
                  <span key={d.id} className="text-xs px-3 py-1 rounded-lg font-medium bg-violet-50 text-violet-600 border border-violet-100">{d.name}</span>
                ))}
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {person.knowledge_base_access && <span className="text-[10px] px-2.5 py-1 rounded-lg font-medium bg-emerald-50 text-emerald-600 border border-emerald-100">Knowledge Base Access</span>}
          </div>
        </div>

        {/* Block / Unblock section (institution only) */}
        {!readOnly && (
          <div className="px-6 pb-5 border-t border-gray-100 pt-4">
            {isBlocked ? (
              <Button onClick={handleUnblock} disabled={unblocking} className="w-full h-10 rounded-xl text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="unblock-student-btn">
                <ShieldCheck className="w-4 h-4 mr-1.5" /> {unblocking ? 'Unblocking...' : 'Unblock Student'}
              </Button>
            ) : !showBlockForm ? (
              <Button onClick={() => { setShowBlockForm(true); send2FA(); }} variant="outline" className="w-full h-10 rounded-xl text-sm font-semibold text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300" data-testid="block-student-btn">
                <ShieldOff className="w-4 h-4 mr-1.5" /> Block Student
              </Button>
            ) : (
              <div className="space-y-3 bg-red-50/50 rounded-xl p-4 border border-red-100" data-testid="block-form">
                <div className="flex items-center gap-2 mb-2">
                  <ShieldAlert className="w-4 h-4 text-red-500" />
                  <p className="text-xs font-semibold text-red-600">Block Student — 2FA Required</p>
                </div>
                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Reason for blocking</label>
                  <textarea value={blockReason} onChange={e => setBlockReason(e.target.value)} rows={2} className="w-full mt-1 text-sm rounded-lg border border-red-200 p-2.5 focus:outline-none focus:ring-2 focus:ring-red-300 resize-none" placeholder="e.g. Violation of academic policy..." data-testid="block-reason-input" />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">2FA Verification Code</label>
                  {devCode && <p className="text-[10px] text-amber-600 mt-0.5">Dev mode: {devCode}</p>}
                  <Input value={tfaCode} onChange={e => setTfaCode(e.target.value)} placeholder="Enter 6-digit code" className="h-9 mt-1 rounded-lg text-sm" data-testid="block-2fa-input" />
                  {!tfaSent && <button onClick={send2FA} className="text-[10px] mt-1 font-medium underline" style={{ color: A }}>Resend code</button>}
                </div>
                <div className="flex gap-2 pt-1">
                  <Button onClick={handleBlock} disabled={blocking} className="flex-1 h-9 rounded-xl text-sm font-semibold bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-block-btn">
                    <Lock className="w-3.5 h-3.5 mr-1" /> {blocking ? 'Blocking...' : 'Confirm Block'}
                  </Button>
                  <Button variant="ghost" onClick={() => { setShowBlockForm(false); setBlockReason(''); setTfaCode(''); }} className="h-9 px-4 rounded-xl text-sm text-gray-500">Cancel</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export function StudentsSection({ students, departments: deptList, batches: batchList, accent, onRevoke, onBlock, onUnblock, readOnly }) {
  const A = accent || '#00AA8D';
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deptFilter, setDeptFilter] = useState('all');
  const [batchFilter, setBatchFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [selectedPerson, setSelectedPerson] = useState(null);

  const departments = useMemo(() => {
    if (deptList && deptList.length) return deptList;
    const deptMap = {};
    students.forEach(s => (s.departments || []).forEach(d => { deptMap[d.id] = d.name; }));
    return Object.entries(deptMap).map(([id, name]) => ({ id, name }));
  }, [students, deptList]);

  const batches = useMemo(() => {
    if (batchList && batchList.length) return batchList;
    const batchMap = {};
    students.forEach(s => (s.batches || []).forEach(b => { batchMap[b.id] = b.name; }));
    return Object.entries(batchMap).map(([id, name]) => ({ id, name }));
  }, [students, batchList]);

  const filtered = useMemo(() => {
    let list = [...students];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(s => s.full_name?.toLowerCase().includes(q) || s.email?.toLowerCase().includes(q) || s.phone?.includes(q));
    }
    if (statusFilter !== 'all') list = list.filter(s => (s.licence_status || 'none') === statusFilter);
    if (deptFilter !== 'all') list = list.filter(s => (s.departments || []).some(d => d.id === deptFilter));
    if (batchFilter !== 'all') list = list.filter(s => (s.batches || []).some(b => b.id === batchFilter));
    list.sort((a, b) => {
      let va, vb;
      if (sortBy === 'name') { va = a.full_name || ''; vb = b.full_name || ''; }
      else if (sortBy === 'enrolled') { va = a.enrolled_classes || 0; vb = b.enrolled_classes || 0; }
      else if (sortBy === 'date') { va = a.created_at || ''; vb = b.created_at || ''; }
      else { va = a.licence_status || ''; vb = b.licence_status || ''; }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [students, search, statusFilter, deptFilter, batchFilter, sortBy, sortDir]);

  const stats = useMemo(() => ({
    total: students.length,
    active: students.filter(s => s.licence_status === 'active').length,
    enrolled: students.filter(s => (s.enrolled_classes || 0) > 0).length,
    pendingEnroll: students.reduce((sum, s) => sum + (s.pending_classes || 0), 0),
  }), [students]);

  const toggleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDir('asc'); }
  };
  const SortIcon = ({ field }) => sortBy === field ? (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />) : null;
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  return (
    <div className="flex flex-col gap-4 h-[calc(100vh-120px)]" data-testid="students-section">
      {selectedPerson && <ProfileModal person={selectedPerson} accent={A} onClose={() => setSelectedPerson(null)} onBlock={onBlock} onUnblock={onUnblock} readOnly={readOnly} />}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 flex-shrink-0">
        {[
          { label: 'Total Students', val: stats.total, icon: GraduationCap, color: A },
          { label: 'Active Licence', val: stats.active, icon: ShieldCheck, color: '#10b981' },
          { label: 'Enrolled', val: stats.enrolled, icon: CheckCircle2, color: '#3b82f6' },
          { label: 'Pending Enroll', val: stats.pendingEnroll, icon: Clock, color: '#f59e0b' },
        ].map(s => (
          <div key={s.label} className="bg-white border border-gray-200 rounded-xl shadow p-4 transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 group" data-testid={`stat-${s.label.toLowerCase().replace(/ /g, '-')}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center transition-transform group-hover:scale-105" style={{ background: `${s.color}14` }}>
                <s.icon className="w-4 h-4" style={{ color: s.color }} strokeWidth={1.75} />
              </div>
              <span className="text-[9px] font-semibold uppercase tracking-wider text-gray-500">{s.label}</span>
            </div>
            <p className="text-2xl font-extrabold text-gray-900 tabular-nums tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{s.val}</p>
          </div>
        ))}
      </div>

      {/* Search + Filters */}
      <div className="bg-white border border-gray-200 rounded-xl shadow p-3 flex-shrink-0">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email, or phone..." className="pl-9 h-9 text-sm rounded-lg border-gray-200" data-testid="student-search" />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-3.5 h-3.5 text-gray-500" />
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="h-9 text-xs rounded-lg border border-gray-200 px-3 bg-white text-gray-700 focus:outline-none" data-testid="student-status-filter">
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="none">No Licence</option>
            </select>
            {departments.length > 0 && (
              <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)} className="h-9 text-xs rounded-lg border border-gray-200 px-3 bg-white text-gray-700 focus:outline-none" data-testid="student-dept-filter">
                <option value="all">All Departments</option>
                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            )}
            {batches.length > 0 && (
              <select value={batchFilter} onChange={e => setBatchFilter(e.target.value)} className="h-9 text-xs rounded-lg border border-gray-200 px-3 bg-white text-gray-700 focus:outline-none" data-testid="student-batch-filter">
                <option value="all">All Batches</option>
                {batches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            )}
          </div>
          <span className="text-[10px] text-gray-500 ml-auto font-medium">{filtered.length} of {students.length}</span>
        </div>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl shadow text-center py-16 flex-1">
          <GraduationCap className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <p className="text-sm text-gray-500 font-medium">{students.length === 0 ? 'No students yet' : 'No students match your filters'}</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl shadow overflow-hidden flex-1 min-h-0 flex flex-col">
          <div className="flex-1 overflow-y-auto min-h-0">
            <table className="w-full text-sm" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              <thead className="bg-gray-50/80 border-b border-gray-100 sticky top-0 z-10">
              <tr>
                <th className="text-left px-5 py-3.5 text-[11px] font-bold text-gray-600 uppercase tracking-wider cursor-pointer select-none" onClick={() => toggleSort('name')}>
                  <span className="flex items-center gap-1">Student <SortIcon field="name" /></span>
                </th>
                <th className="text-left px-5 py-3.5 text-[11px] font-bold text-gray-600 uppercase tracking-wider">Department</th>
                <th className="text-left px-5 py-3.5 text-[11px] font-bold text-gray-600 uppercase tracking-wider cursor-pointer select-none" onClick={() => toggleSort('enrolled')}>
                  <span className="flex items-center gap-1">Enrollment <SortIcon field="enrolled" /></span>
                </th>
                <th className="text-left px-5 py-3.5 text-[11px] font-bold text-gray-600 uppercase tracking-wider cursor-pointer select-none" onClick={() => toggleSort('status')}>
                  <span className="flex items-center gap-1">Licence <SortIcon field="status" /></span>
                </th>
                <th className="text-left px-5 py-3.5 text-[11px] font-bold text-gray-600 uppercase tracking-wider cursor-pointer select-none" onClick={() => toggleSort('date')}>
                  <span className="flex items-center gap-1">Joined <SortIcon field="date" /></span>
                </th>
                <th className="text-right px-5 py-3.5 text-[11px] font-bold text-gray-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => (
                <tr key={s.id} className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors" data-testid={`student-row-${s.id}`}>
                  <td className="px-5 py-3.5">
                    <button onClick={() => setSelectedPerson(s)} className="flex items-center gap-3 hover:opacity-80 transition-opacity text-left" data-testid={`student-click-${s.id}`}>
                      {s.profile_photo
                        ? <img src={s.profile_photo} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                        : <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ background: `${A}cc` }}>{(s.full_name || '?')[0].toUpperCase()}</div>}
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">{s.full_name}</p>
                        <p className="text-[11px] text-gray-500">{s.email}</p>
                      </div>
                    </button>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex flex-wrap gap-1">
                      {(s.departments || []).length > 0
                        ? s.departments.map(d => <span key={d.id} className="text-[10px] px-2 py-0.5 rounded-md font-medium bg-violet-50 text-violet-600 border border-violet-100">{d.name}</span>)
                        : <span className="text-[10px] text-gray-300 italic">None</span>}
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2">
                      {(s.enrolled_classes || 0) > 0 && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-md bg-blue-50 text-blue-600 border border-blue-100">
                          <BookOpen className="w-3 h-3" /> {s.enrolled_classes}
                        </span>
                      )}
                      {(s.pending_classes || 0) > 0 && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-md bg-amber-50 text-amber-600 border border-amber-100">
                          <AlertCircle className="w-3 h-3" /> {s.pending_classes} pending
                        </span>
                      )}
                      {(s.enrolled_classes || 0) === 0 && (s.pending_classes || 0) === 0 && (
                        <span className="text-[10px] text-gray-400 italic">Not enrolled</span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-1.5">
                      <StatusBadge status={s.licence_status || 'none'} />
                      {s.blocked && <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold border bg-red-50 text-red-600 border-red-200">Blocked</span>}
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-xs text-gray-500">{formatDate(s.created_at)}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => setSelectedPerson(s)} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors group" data-testid={`info-${s.id}`} title="View profile">
                        <Info className="w-4 h-4 text-gray-500 group-hover:text-gray-800 transition-colors" />
                      </button>
                      {!readOnly && s.licence_status === 'active' && (
                        <button onClick={() => onRevoke(s.id)} className="text-[11px] text-red-500 hover:text-red-700 font-medium hover:underline transition-colors" data-testid={`revoke-${s.id}`}>
                          Revoke
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
